import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { config } from '../../utils/config';
import { trackAuth } from '../../utils/analytics';
import { useNavigateWithSearchContext } from '../../hooks/useNavigateWithSearchContext';
import './AuthStyles.css';

const AuthButton: React.FC = () => {
  const { user, signOut, session } = useAuth();
  const navigate = useNavigate();
  const navigateWithContext = useNavigateWithSearchContext();
  const [credits, setCredits] = useState<number | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown && userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Load cached credits on mount
  useEffect(() => {
    const cachedCredits = localStorage.getItem('zoningpal_credits');
    if (cachedCredits) {
      setCredits(parseInt(cachedCredits));
    }
  }, []);

  // Fetch user credits
  useEffect(() => {
    if (user && session) {
      fetchCredits();
    } else {
      setCredits(null);
    }
  }, [user, session]);

  // Handle payment return - check for session_id parameter
  useEffect(() => {
    if (user && session) {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const openAccount = urlParams.get('open_account');
      
      if (sessionId && openAccount === 'true') {
        // Navigate to account page automatically
        navigate('/account/overview');
        
        // Clean up URL parameters
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  }, [user, session, navigate]);

  // Handle showLogin parameter from pricing page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const showLogin = urlParams.get('showLogin');
    
    if (showLogin === 'true' && !user) {
      // Navigate to login page automatically
      navigate('/login');
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [user, navigate]);

  const fetchCredits = async () => {
    try {
      setCreditsLoading(true);
      
      // Try to get cached credits first
      const cachedCredits = localStorage.getItem('zoningpal_credits');
      if (cachedCredits && !credits) {
        setCredits(parseInt(cachedCredits));
      }
      
      const response = await fetch(`${config.API_URL}/api/payments-simple/account`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const newCredits = data.account?.credits_balance || 0;
        setCredits(newCredits);
        // Cache the credits
        localStorage.setItem('zoningpal_credits', newCredits.toString());
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
      // Don't reset credits on error, keep cached value
    } finally {
      setCreditsLoading(false);
    }
  };



  const handleLoginNavigation = () => {
    navigate('/login');
  };

  const handleSignUpNavigation = () => {
    navigate('/login?mode=signup');
  };

  const handleSignOut = async () => {
    trackAuth('logout');
    await signOut();
    localStorage.removeItem('zoningpal_credits'); // Clear cached credits on sign out
    navigate('/');
    setShowDropdown(false);
  };



  return (
    <div className="auth-button-container">
      {user ? (
        <div className="user-auth-section">
          {/* Credits Display */}
          {credits !== null && (
            <div className="credits-display">
              <span className="credits-text">
                {creditsLoading ? '...' : `${credits} Credits`}
              </span>
            </div>
          )}
          
          {/* User Menu */}
          <div className="user-menu" ref={userMenuRef}>
            <button 
              className="auth-button user-button"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <span className="user-initial">{user.email?.[0].toUpperCase() || 'U'}</span>
            </button>
            {showDropdown && (
              <div className="user-dropdown">
                <div className="user-email">{user.email}</div>
                
                <button className="dropdown-menu-item" onClick={() => {
                  navigate('/account/settings');
                  setShowDropdown(false);
                }}>
                  Manage account
                </button>
                
                <button className="dropdown-menu-item" onClick={() => {
                  navigateWithContext('/account/pricing');
                  setShowDropdown(false);
                }}>
                  Plans & Pricing
                </button>
                
                <button className="dropdown-menu-item" onClick={() => {
                  navigate('/account/reports');
                  setShowDropdown(false);
                }}>
                  My Reports
                </button>
                
                <button className="dropdown-menu-item sign-out" onClick={() => {
                  handleSignOut();
                  setShowDropdown(false);
                }}>
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <button 
            className="auth-button sign-in-button" 
            onClick={handleLoginNavigation}
          >
            Sign In
          </button>
          <button 
            className="auth-button sign-up-button" 
            onClick={handleSignUpNavigation}
          >
            Sign Up
          </button>
        </>
      )}
      

    </div>
  );
};

export default AuthButton;
